import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThumbsUp, ThumbsDown, PlusCircle, Lock, Pencil, Users, RefreshCw, Trophy } from 'lucide-react';
import { NominateCandidate } from './NominateCandidate';
import { ShareButtons } from '../SharePoll/ShareButtons';
import { formatTimeRemaining } from '../../utils/dateUtils';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../hooks/useToast';
import type { Poll, VoteResult } from '../../types';

interface ResultsProps {
  poll: Poll;
  results: VoteResult[];
  userId: string;
  isAuthor: boolean;
  onVoteChange?: (candidateId: string, vote: boolean) => void;
  onNominateCandidate?: (names: string[]) => void;
  onUpdateSettings?: (settings: Partial<Poll['settings']>) => void;
}

interface Position {
  top: number;
  left: number;
  height: number;
  width: number;
}

export function Results({ 
  poll,
  results: initialResults, 
  userId,
  isAuthor,
  onVoteChange, 
  onNominateCandidate,
  onUpdateSettings
}: ResultsProps) {
  const navigate = useNavigate();
  const [showNominateForm, setShowNominateForm] = useState(false);
  const [results, setResults] = useState(initialResults);
  const [voterCount, setVoterCount] = useState(0);
  const [hasVoted, setHasVoted] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeVoteId, setActiveVoteId] = useState<string | null>(null);
  const { showToast } = useToast();
  const [winner, setWinner] = useState<VoteResult[] | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const positionsRef = useRef<Map<string, Position>>(new Map());
  const [isPollClosed, setIsPollClosed] = useState(false);

  useEffect(() => {
    const checkPollStatus = () => {
      const isClosed = poll.is_closed || 
        (poll.expires_at && new Date(poll.expires_at) <= new Date());
      setIsPollClosed(isClosed);
    };

    checkPollStatus();
    const interval = setInterval(checkPollStatus, 1000);
    return () => clearInterval(interval);
  }, [poll.is_closed, poll.expires_at]);

  useEffect(() => {
    const fetchVoterCount = async () => {
      try {
        const { data: votes, error } = await supabase
          .from('votes')
          .select('voter_id')
          .eq('poll_id', poll.id);

        if (error) throw error;
        if (votes) {
          const uniqueVoters = new Set(votes.map(v => v.voter_id));
          setVoterCount(uniqueVoters.size);
          setHasVoted(votes.some(v => v.voter_id === userId));
        }
      } catch (error) {
        console.error('Error fetching voter count:', error);
      }
    };

    fetchVoterCount();
  }, [poll.id, userId]);

  useEffect(() => {
    // Determine winner
    if (results.length > 0) {
      const highestTotal = results[0]?.total;
      const tiedResults = results.filter(r => r.total === highestTotal);
      
      if (tiedResults.length > 0 && highestTotal !== 0 && (tiedResults.length === 1 || highestTotal > (results[tiedResults.length]?.total ?? -Infinity))) {
        setWinner(tiedResults);
      } else {
        setWinner(null);
      }
    }
  }, [results, isPollClosed]);

  // FLIP animation helpers
  const recordPositions = () => {
    itemRefs.current.forEach((element, id) => {
      const rect = element.getBoundingClientRect();
      positionsRef.current.set(id, {
        top: rect.top,
        left: rect.left,
        height: rect.height,
        width: rect.width
      });
    });
  };

  const playFlipAnimation = () => {
    requestAnimationFrame(() => {
      itemRefs.current.forEach((element, id) => {
        const firstPosition = positionsRef.current.get(id);
        if (!firstPosition) return;

        const lastPosition = element.getBoundingClientRect();
        const deltaY = firstPosition.top - lastPosition.top;

        // Invert
        element.style.transform = `translateY(${deltaY}px)`;
        element.style.transition = 'none';

        // Play
        requestAnimationFrame(() => {
          element.style.transition = 'transform 0.3s ease-out';
          element.style.transform = 'translateY(0)';
        });
      });
    });
  };

  const handleRefreshResults = async () => {
    if (isRefreshing) return;

    try {
      setIsRefreshing(true);
      const { data: votes, error } = await supabase
        .from('votes')
        .select('voter_id, candidate_id, vote')
        .eq('poll_id', poll.id);

      if (error) throw error;

      if (votes) {
        const uniqueVoters = new Set(votes.map(v => v.voter_id));
        setVoterCount(uniqueVoters.size);

        // Record positions before update
        recordPositions();

        const updatedResults = results.map(result => {
          const candidateVotes = votes.filter(v => v.candidate_id === result.candidateId);
          const upvotes = candidateVotes.filter(v => v.vote).length;
          const downvotes = candidateVotes.filter(v => !v.vote).length;
          
          return {
            ...result,
            upvotes,
            downvotes,
            total: upvotes - downvotes
          };
        });

        updatedResults.sort((a, b) => {
          if (b.total !== a.total) {
            return b.total - a.total;
          }
          return a.candidateName.localeCompare(b.candidateName);
        });

        setResults(updatedResults);
        
        // Play animation after state update
        requestAnimationFrame(playFlipAnimation);
        showToast('Results refreshed');
      }
    } catch (error) {
      console.error('Error refreshing results:', error);
      showToast('Failed to refresh results');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleVoteChange = async (candidateId: string, vote: boolean) => {
    if (!onVoteChange || isPollClosed) return;

    try {
      // Record positions before update
      recordPositions();

      // Check single upvote restriction
      if (vote && poll.settings.singleUpvoteOnly) {
        const existingUpvote = Object.entries(poll.votes[userId] || {}).find(
          ([cId, isUpvote]) => isUpvote && cId !== candidateId
        );

        if (existingUpvote) {
          const confirmChange = window.confirm(
            "Votes are restricted to a single candidate in this poll. Do you want to change your vote to this candidate?"
          );
          if (!confirmChange) {
            return;
          }

          // Remove the previous upvote first
          await onVoteChange(existingUpvote[0], false);

          // Update local state for the previous upvote
          setResults(prevResults => {
            return prevResults.map(result => {
              if (result.candidateId === existingUpvote[0]) {
                return {
                  ...result,
                  upvotes: Math.max(0, result.upvotes - 1),
                  total: Math.max(0, result.total - 1)
                };
              }
              return result;
            });
          });

          // Update votes in poll object
          poll.votes[userId] = {
            ...poll.votes[userId],
            [existingUpvote[0]]: false
          };
        }
      }

      // Set active vote for animation
      setActiveVoteId(candidateId);

      // Update local state for the current vote
      setResults(prevResults => {
        const updatedResults = prevResults.map(result => {
          if (result.candidateId === candidateId) {
            const oldVote = poll.votes[userId]?.[candidateId];
            let upvotes = result.upvotes;
            let downvotes = result.downvotes;

            // Remove old vote if it exists
            if (oldVote !== undefined) {
              if (oldVote) upvotes = Math.max(0, upvotes - 1);
              else downvotes = Math.max(0, downvotes - 1);
            }

            // Add new vote
            if (vote) upvotes++;
            else downvotes++;

            return {
              ...result,
              upvotes,
              downvotes,
              total: upvotes - downvotes
            };
          }
          return result;
        });

        // Sort by total votes
        return updatedResults.sort((a, b) => {
          if (b.total !== a.total) {
            return b.total - a.total;
          }
          return a.candidateName.localeCompare(b.candidateName);
        });
      });

      // Play animation after state update
      requestAnimationFrame(playFlipAnimation);

      // Update votes in poll object
      poll.votes[userId] = {
        ...poll.votes[userId],
        [candidateId]: vote
      };

      // Update the vote in the database
      await onVoteChange(candidateId, vote);

      // Clear active vote after animation
      setTimeout(() => {
        setActiveVoteId(null);
      }, 300);

      // Refresh results to ensure consistency
      await handleRefreshResults();
    } catch (error) {
      console.error('Error updating vote:', error);
      showToast('Failed to update vote');
    }
  };

  const handleNominate = async (names: string[]) => {
    const uniqueNames = names
      .map(name => name.trim())
      .filter(name => name && !poll.candidates.some(c => 
        c.name.toLowerCase() === name.toLowerCase()
      ));
    
    if (uniqueNames.length > 0) {
      onNominateCandidate?.(uniqueNames);
    }
    setShowNominateForm(false);
  };

  const handleShowVoteDetails = (result: VoteResult) => {
    showToast(`${result.upvotes} upvotes, ${result.downvotes} downvotes`);
  };

  const timeRemaining = poll.settings.expiresAt && !isPollClosed
    ? formatTimeRemaining(new Date(poll.settings.expiresAt))
    : null;

  const pollUrl = `${window.location.origin}/${poll.id}`;

  return (
    <div className="w-full max-w-md mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-50">{poll.name}</h2>
          {winner && (
            <p className="text-lg text-blue-600 dark:text-blue-400 mt-2 flex items-center gap-2 flex-wrap">
              <Trophy className="w-5 h-5" />
              {winner.length === 1 ? (
                <><i>{winner[0].candidateName}</i> {isPollClosed ? 'is the winner!' : 'is currently in the lead!'}</>
              ) : (
                <span>
                  {winner.map((w, i) => (
                    <React.Fragment key={w.candidateId}>
                      {i > 0 && ' and '}
                      <i>{w.candidateName}</i>
                    </React.Fragment>
                  ))}
                  {' '}are {isPollClosed ? 'tied for the win!' : 'tied for the lead!'}
                </span>
              )}
            </p>
          )}
          {!winner && isPollClosed && (
            <p className="text-lg text-gray-600 dark:text-gray-400 mt-2">
              Poll ended with no votes
            </p>
          )}
        </div>
        {isAuthor && !isPollClosed && (
          <button
            onClick={() => navigate(`/${poll.id}/edit`)}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            title="Edit Poll"
          >
            <Pencil className="w-5 h-5" />
          </button>
        )}
      </div>

      {isPollClosed ? (
        <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 mb-4">
          <Lock className="w-4 h-4" />
          <span>Poll Closed</span>
        </div>
      ) : timeRemaining ? (
        <p className="text-center text-gray-500 dark:text-gray-400 mb-4">
          Time remaining: {timeRemaining}
        </p>
      ) : null}
      
      <div ref={listRef} className="relative space-y-2 mb-6">
        <div className="flex items-center gap-4 px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
          <span className="w-24">{!isPollClosed ? 'Your votes' : 'Final votes'}</span>
          <span className="w-12 text-center">Rank</span>
          <span>Candidate</span>
          <span className="ml-auto">Votes</span>
        </div>

        {results.map((result, index) => {
          const prevTotal = results[index - 1]?.total ?? Infinity;
          const rank = prevTotal === result.total ? index : index + 1;
          const isWinner = winner?.some(w => w.candidateId === result.candidateId);

          return (
            <div
              key={result.candidateId}
              ref={el => {
                if (el) itemRefs.current.set(result.candidateId, el);
              }}
              className={`flip-list-item bg-white dark:bg-gray-900 rounded-lg shadow p-3 flex items-center ${
                isWinner ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''
              }`}
              title={isPollClosed ? `Final result: +${result.upvotes} / -${result.downvotes}` : undefined}
            >
              <div className="w-24 flex items-center gap-2">
                {!isPollClosed && onVoteChange && (
                  <button
                    onClick={() => handleVoteChange(result.candidateId, false)}
                    className={`p-2 rounded-full transition-all ${
                      poll.votes[userId]?.[result.candidateId] === false
                        ? 'bg-red-200 dark:bg-red-900 ring-2 ring-red-500'
                        : 'hover:bg-red-100 dark:hover:bg-red-800'
                    } ${
                      activeVoteId === result.candidateId
                        ? 'scale-110'
                        : ''
                    }`}
                  >
                    <ThumbsDown className="w-4 h-4 text-red-500 dark:text-red-400" />
                  </button>
                )}

                {!isPollClosed && onVoteChange && (
                  <button
                    onClick={() => handleVoteChange(result.candidateId, true)}
                    className={`p-2 rounded-full transition-all ${
                      poll.votes[userId]?.[result.candidateId] === true
                        ? 'bg-green-200 dark:bg-green-900 ring-2 ring-green-500'
                        : 'hover:bg-green-100 dark:hover:bg-green-800'
                    } ${
                      activeVoteId === result.candidateId
                        ? 'scale-110'
                        : ''
                    }`}
                  >
                    <ThumbsUp className="w-4 h-4 text-green-500 dark:text-green-400" />
                  </button>
                )}
                {isPollClosed && (
                  <div className="flex items-center gap-1 text-sm">
                    {poll.votes[userId]?.[result.candidateId] === true && (
                      <ThumbsUp className="w-4 h-4 text-green-500 dark:text-green-400" />
                    )}
                    {poll.votes[userId]?.[result.candidateId] === false && (
                      <ThumbsDown className="w-4 h-4 text-red-500 dark:text-red-400" />
                    )}
                  </div>
                )}
              </div>
              
              <span className="w-12 text-center text-gray-500 dark:text-gray-400 tabular-nums">
                {rank}.
              </span>
              
              <span className="flex-1 font-medium text-gray-900 dark:text-gray-50">
                {result.candidateName}
              </span>
              
              <div className="text-right">
                <span className="text-green-600 dark:text-green-400">+{result.upvotes}</span>
                {' / '}
                <span className="text-red-600 dark:text-red-400">-{result.downvotes}</span>
              </div>
            </div>
          );
        })}
      </div>

      {!isPollClosed && (
        <div className="space-y-3">
          <button
            onClick={handleRefreshResults}
            className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 py-3 px-4 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            disabled={isRefreshing}
          >
            <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh Results'}
          </button>

          <a
            href={`/${poll.id}`}
            className="w-full flex items-center justify-center gap-2 bg-blue-500 text-white py-3 px-4 rounded-lg hover:bg-blue-600"
          >
            Review and Change Votes
          </a>

          {poll.settings.allowNominations && onNominateCandidate && (
            <>
              <button
                onClick={() => setShowNominateForm(true)}
                className="w-full flex items-center justify-center gap-2 bg-green-500 text-white py-3 px-4 rounded-lg hover:bg-green-600"
              >
                <PlusCircle className="w-5 h-5" />
                Nominate a Candidate
              </button>

              {showNominateForm && (
                <NominateCandidate
                  onNominate={handleNominate}
                  onCancel={() => setShowNominateForm(false)}
                />
              )}
            </>
          )}
        </div>
      )}

      <div className="mt-8 space-y-4">
        <p className="text-center text-gray-500 dark:text-gray-400">Poll Code: {poll.id}</p>
        
        <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
          <Users className="w-4 h-4" />
          <span>
            {voterCount} {voterCount === 1 ? 'voter has' : 'voters have'} voted in this poll
          </span>
        </div>
        
        {(isAuthor || poll.settings.showSharingIcons) && (
          <>
            <p className="text-center text-gray-600 dark:text-gray-300 font-medium">
              {!isPollClosed ? 'Invite more voters' : 'Share these results'}
            </p>
            <ShareButtons 
              pollUrl={pollUrl} 
              pollName={poll.name}
              shareText={isPollClosed ? `Check out the results to the "${poll.name}" poll` : undefined}
            />
          </>
        )}
      </div>
    </div>
  );
}